<template>
	<div class="top-nav">
		<HeadwayWidget />

		<v-menu
			v-model="userMenuOpen"
			content-class="arrow-menu user-dropdown-menu"
			location="bottom right"
			:close-on-content-click="true"
			origin="top right"
			transition="slide-y-transition"
		>
			<template v-slot:activator="{ props }">
				<div
					class="user-menu-activator"
					v-bind="props"
				>
					<User16 class="sgnl-icon-color-secondary" />
					<div
						v-if="!$vuetify.display.mobile"
						class="user-menu-info"
					>
						<span class="sgnl-caption-medium sgnl-text-color-default">{{
							userStore.currentUser?.name || userEmail
						}}</span>
						<span
							v-if="organizationName"
							class="sgnl-caption-regular sgnl-text-color-secondary"
							>{{ organizationName }}</span
						>
					</div>
					<ChevronUp16
						v-if="userMenuOpen"
						class="sgnl-icon-color-secondary"
					/>
					<ChevronDown16
						v-else
						class="sgnl-icon-color-secondary"
					/>
				</div>
			</template>
			<v-list
				density="compact"
				class="user-dropdown-list"
			>
				<div class="user-dropdown-version-header">
					<span class="sgnl-paragraph-s-medium sgnl-text-color-secondary"
						>Signaloid Cloud Developer Platform</span
					>
					<span class="sgnl-caption-regular sgnl-text-color-tertiary"
						>Version <code>{{ appVersion }}</code></span
					>
				</div>
				<v-divider class="user-dropdown-divider" />
				<v-list-item>
					<v-btn
						variant="text"
						:to="'/settings/profile'"
					>
						<Settings16 class="sgnl-icon-color-secondary" />
						Profile Settings
					</v-btn>
				</v-list-item>
				<v-divider class="user-dropdown-divider" />
				<v-list-item>
					<v-btn
						variant="text"
						href="https://legal.signaloid.io/signaloid-cloud-service-agreement?_gl=1*z69gaj*_gcl_au*OTYyMjQ3NzAyLjE3NzA3NDUwMzQuMTkxNDQ5MjE1Ni4xNzcxMDIyNjI5LjE3NzEwMjY1ODM.*_ga*MjAxNTEyMjY5My4xNzYxNDA5NDA2*_ga_HZ5WHLGE48*czE3NzEwMjI2MDckbzgkZzEkdDE3NzEwMjY1ODMkajYwJGwwJGgw"
						target="_blank"
					>
						<Document16 class="sgnl-icon-color-secondary" />
						Service Agreement
						<ArrowUpRight16 class="sgnl-icon-color-secondary" />
					</v-btn>
				</v-list-item>
				<v-list-item>
					<v-btn
						variant="text"
						href="https://legal.signaloid.io/privacy-policy?_gl=1*15hshyk*_gcl_au*OTYyMjQ3NzAyLjE3NzA3NDUwMzQuMTkxNDQ5MjE1Ni4xNzcxMDIyNjI5LjE3NzEwMjY1ODM.*_ga*MjAxNTEyMjY5My4xNzYxNDA5NDA2*_ga_HZ5WHLGE48*czE3NzEwMjI2MDckbzgkZzEkdDE3NzEwMjY1ODMkajYwJGwwJGgw"
						target="_blank"
					>
						<Locked16 class="sgnl-icon-color-secondary" />
						Privacy Policy
						<ArrowUpRight16 class="sgnl-icon-color-secondary" />
					</v-btn>
				</v-list-item>
				<v-list-item>
					<v-btn
						variant="text"
						href="https://legal.signaloid.io/cookie-policy?_gl=1*16aiubn*_gcl_au*OTYyMjQ3NzAyLjE3NzA3NDUwMzQuMTkxNDQ5MjE1Ni4xNzcxMDIyNjI5LjE3NzEwMjY1ODM.*_ga*MjAxNTEyMjY5My4xNzYxNDA5NDA2*_ga_HZ5WHLGE48*czE3NzEwMjI2MDckbzgkZzEkdDE3NzEwMjY1ODMkajYwJGwwJGgw"
						target="_blank"
					>
						<Cookie16 class="sgnl-icon-color-secondary" />
						Cookie Policy
						<ArrowUpRight16 class="sgnl-icon-color-secondary" />
					</v-btn>
				</v-list-item>
				<v-divider class="user-dropdown-divider" />
				<v-list-item>
					<v-btn
						color="error"
						variant="text"
						v-on:click="$emit('sign-out')"
					>
						<Logout16 class="sgnl-icon-color-error" />
						Log Out
					</v-btn>
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
// Components
import HeadwayWidget from "@/components/HeadwayWidget.vue";
// Carbon Icons
import ArrowUpRight16 from "@carbon/icons-vue/es/arrow--up-right/16";
import User16 from "@carbon/icons-vue/es/user/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";
import ChevronUp16 from "@carbon/icons-vue/es/chevron--up/16";
import Settings16 from "@carbon/icons-vue/es/settings/16";
import Locked16 from "@carbon/icons-vue/es/locked/16";
import Cookie16 from "@carbon/icons-vue/es/cookie/16";
import Logout16 from "@carbon/icons-vue/es/logout/16";
import Document16 from "@carbon/icons-vue/es/document/16";

// Utilities
import { sdk } from "@/js/sdk";
import { monitoringCaptureError } from "@/plugins/monitoring";

// Libraries
import { fetchAuthSession } from "aws-amplify/auth";

// Stores
import { useUserStore } from "@/stores/user";

// Types
const legalLinks: LegalLinkObject[] = [
	{
		title: "Signaloid Cloud Service Agreement",
		icon: "document",
		url: "https://legal.signaloid.io/signaloid-cloud-service-agreement.html",
	},
	{
		title: "Privacy Policy",
		icon: "locked",
		url: "https://legal.signaloid.io/privacy-policy.html",
	},
	{
		title: "Cookie Policy",
		icon: "cookie",
		url: "https://legal.signaloid.io/cookie-policy.html",
	},
];

type LegalLinkObject = {
	title: string;
	icon: string;
	url: string;
};
type ComponentData = {
	legalLinks: LegalLinkObject[];
	session: null | any;
	userFullname: string;
	userEmail: string;
	appVersion: string;
	organizationId: string | null;
	organizationName: string;
	userMenuOpen: boolean;
};

export default defineComponent({
	name: "TopNav",
	components: {
		HeadwayWidget,
		ArrowUpRight16,
		User16,
		ChevronDown16,
		ChevronUp16,
		Settings16,
		Locked16,
		Cookie16,
		Logout16,
		Document16,
	},
	data: (): ComponentData => ({
		legalLinks: legalLinks,
		session: null,
		userFullname: "",
		userEmail: "",
		appVersion: "",
		organizationId: null,
		organizationName: "",
		userMenuOpen: false,
	}),
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	emits: {
		"sign-out": () => true,
	},
	methods: {},
	async created() {
		try {
			// TODO: Fix Type instantiation is excessively deep and possibly infinite.ts-plugin(2589)
			this.session = await fetchAuthSession();
			this.userFullname = (this.session.tokens?.idToken?.payload?.["name"] as string) ?? "";
			this.userEmail = (this.session.tokens?.idToken?.payload?.["email"] as string) ?? "";

			// Get organization ID from user store
			const userStore = useUserStore();
			this.organizationId = await userStore.getCurrentUserOrganizationID();

			// Fetch organization name
			if (this.organizationId) {
				try {
					const response = await sdk.organizations.get(this.organizationId);
					this.organizationName = response?.Name || "";
				} catch (error) {
					console.error("Failed to fetch organization details:", error);
				}
			}
		} catch (error) {
			monitoringCaptureError(error, "Get current user details");
			window.location.href = "/";
		}
		const version = import.meta.env.VITE_VERSION ?? "Unknown";
		const hash = import.meta.env.VITE_COMMIT_HASH?.slice(0, 7) ?? null;
		if (hash === null) {
			this.appVersion = version;
		} else {
			this.appVersion = `${version}+${hash}`;
		}
	},
});
</script>

<style>
.expand-transition-enter-active,
.expand-transition-leave-active,
.slide-y-transition-enter-active,
.slide-y-transition-leave-active {
	transition-duration: 0.15s;
}

.arrow-menu {
}
.top-nav {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding-right: var(--sgnl-space-32);
	gap: var(--sgnl-space-24);
}

.top-nav #HW_badge {
	left: calc(70% - var(--sgnl-space-16));
	top: -6px;
}

@media (max-width: 959px) {
	.top-nav #HW_badge {
		left: calc(70% - var(--sgnl-space-10));
		top: 1px;
	}
}
.text-menu {
	font-size: var(--sgnl-font-size-caption);
}

.user-menu-activator {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
	cursor: pointer;
}

.user-menu-info {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}

.btn-tertiary {
	display: flex;
	height: var(--sgnl-space-32);
	padding: var(--sgnl-space-8) 0;
	justify-content: flex-start;
	align-items: center;
	gap: var(--sgnl-space-8);
	background: transparent;
	border: none;
	box-shadow: none;
	width: 100%;
}

.btn-tertiary .v-btn__content {
	color: var(--sgnl-default-secondary-text);
	text-align: center;
	font-size: var(--sgnl-font-size-paragraph-s);
	font-style: normal;
	font-weight: 500;
	line-height: var(--sgnl-font-line-height-paragraph-s);
	letter-spacing: var(--sgnl-font-letter-spacing-0);
	gap: var(--sgnl-space-8);
}

.btn-tertiary:hover {
	background: transparent;
}

.btn-tertiary .v-btn__overlay,
.btn-tertiary .v-btn__underlay {
	display: none;
}

.btn-tertiary:hover .v-btn__content {
	text-decoration: underline;
}

.btn-tertiary:hover .v-btn__content > span {
	text-decoration: none;
}

.btn-tertiary-destructive {
	display: flex;
	height: var(--sgnl-space-32);
	padding: var(--sgnl-space-8) 0;
	justify-content: flex-start;
	align-items: center;
	gap: var(--sgnl-space-8);
	background: transparent;
	border: none;
	box-shadow: none;
	width: 100%;
}

.btn-tertiary-destructive .v-btn__content {
	color: var(--sgnl-alert-error-text);
	text-align: center;
	font-size: var(--sgnl-font-size-paragraph-s);
	font-style: normal;
	font-weight: 500;
	line-height: var(--sgnl-font-line-height-paragraph-s);
	letter-spacing: var(--sgnl-font-letter-spacing-0);
	gap: var(--sgnl-space-8);
}

.btn-tertiary-destructive:hover {
	background: transparent;
}

.btn-tertiary-destructive .v-btn__overlay,
.btn-tertiary-destructive .v-btn__underlay {
	display: none;
}

.btn-tertiary-destructive:hover .v-btn__content {
	text-decoration: underline;
}

.btn-tertiary-destructive:hover .v-btn__content > span {
	text-decoration: none;
}

.user-dropdown-list .user-dropdown-divider {
	border-color: var(--sgnl-default-primary-border);
	opacity: 1;
}

.user-dropdown-list {
	padding: var(--sgnl-space-4) 0;
}

.user-dropdown-version-header {
	display: flex;
	flex-direction: column;
	padding: var(--sgnl-space-4) var(--sgnl-space-16) var(--sgnl-space-8);
}

@media (max-width: 959px) {
	.top-nav {
		padding-left: 0;
		padding-right: 0;
		gap: 0;
	}

	.user-menu-activator,
	.top-nav .defaultWidgetSelector {
		min-height: 44px;
		min-width: 44px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.top-nav .defaultWidgetSelector {
		margin-right: 0;
	}
}
</style>
